/* eslint-disable radix */
/* eslint-disable no-restricted-properties */
import React from 'react';

import { format, addDays, differenceInCalendarDays, endOfDay, subDays } from 'date-fns';
import moment from 'moment';

import * as DOCUMENT from '@constants/documentation';
import {
  SETTLED as STATUS_SETTLED,
  SCHEDULED as STATUS_SCHEDULED,
  TYPE_CARD,
  getMethod,
} from '@constants/transactions';
import { REGIONS, regionPhoneCode, currencySymbol } from '@constants/regions';
import Text from '@components/Text';
import { getEnv } from '@config';
import NAV from '@constants/nav';
import { sellerTypes } from '@hooks/useAuth';
import { ROLES_LIST_PRIORITY } from '@constants/user';
import get from 'lodash.get';
import { store } from '@store/store';
import { CONFIG_MERCHANT_TYPE } from '@constants/merchantConfigType';
import { CMR_POINTS } from '@hooks/useRefund';

const { country, widgetEvent, widgetTag, paymentMethodsValidToRefund, balancesWindowDays } = getEnv();

export const hasEnableBanksAccount = (banks) => {
  const aux = banks.filter((elm) => elm.enabled);

  return aux.length !== 0;
};

export const getEnabledBankAccount = (banks) => {
  const aux = banks.filter((elm) => elm.enabled);
  if (aux.length === 0) return null;

  return aux[0];
};

export const getObjectRefund = (
  merchantUuid,
  authorizationId,
  amount,
  merchantId,
  transactionStatus,
  detailsTransaction
) => {
  merchantId = String(merchantId);
  amount = Number(amount);
  const request = {
    merchantUuid,
    authorizationId,
    body: {
      merchant_id: merchantId,
      amount,
      transaction_status: transactionStatus,
    },
  };
  if (detailsTransaction) {
    const { payment_method: paymentMethod, intention_id: intentionId } = detailsTransaction;
    request.body = {
      ...request.body,
      is_point: paymentMethod === CMR_POINTS,
      intention_id: intentionId,
      state: detailsTransaction.state?.state || null,
    };
  }
  return request;
};

export const formatMoneyWithPointGrouping = (amount) =>
  new Intl.NumberFormat('es-CL', { maximumFractionDigits: 0 }).format(amount);

/** Functions getFullAmount & formatMoney deprecated: use moneyFormat */

export const getTotalAmount = (transactions) => {
  let rta = 0;
  transactions.forEach((elm) => {
    if (elm.gross_value > 0) rta += elm.gross_value;
  });
  return rta;
};

export const convertMoneyToNumber = (value) => {
  const { decimalSeparator, thousandSeparator } = REGIONS[country];
  const removeAll = (str, sub) => (sub ? String(str).split(sub).join('') : String(str));
  const replaceAll = (str, from, to) => (from ? String(str).split(from).join(to) : String(str));

  let result = String(value);
  result = removeAll(result, currencySymbol);
  result = removeAll(result, thousandSeparator);
  result = replaceAll(result, decimalSeparator, '.');
  return result;
};

export const reverseFullAmount = (value) => {
  const { decimalCount } = REGIONS[country];

  if (value.length === 1) return Number(value) / Math.pow(10, decimalCount);

  let money = convertMoneyToNumber(value);
  if (money.toString().includes('.')) {
    const splitAmount = money.toString().split('.');
    money = Number(splitAmount[0] + splitAmount[1]) / Math.pow(10, decimalCount);
  }

  return Math.abs(Number(money)).toFixed(decimalCount);
};

export const getDocSubtitle = (elm, className) => (
  <>
    <Text
      element="p"
      color="gray700"
      smallType="Subtitle-1-w400"
      type="Body-2-w400"
      className={className}
    >
      {typeof elm.preLink === 'string' ? elm.preLink : ''}{' '}
      {typeof elm.link === 'string' && (
        <Text
          element="a"
          color="gray800"
          target="_blank"
          rel="noopener noreferrer"
          style={{ overflowWrap: 'break-word' }}
          href={elm.link}
        >
          {elm.linkName ? elm.linkName : elm.link}
        </Text>
      )}
      {typeof elm.postLink === 'string' ? elm.postLink : ''}
    </Text>
    {elm.optional && (
      <Text element="span" className="opcional" type="Overline-w400" style={{ textAlign: 'left' }}>
        *Opcional
      </Text>
    )}
  </>
);

export const objectBalances = (id, daysFrom = balancesWindowDays, daysTo = balancesWindowDays) => {
  const now = new Date();
  return {
    id,
    start: getFormatedDate(subDays(now, daysFrom)),
    end: getFormatedDate(addDays(now, daysTo)),
  };
};

export const getRowTransactionsStatus = (status) => {
  if (status === STATUS_SETTLED) return 'Aprobado';
  if (status === STATUS_SCHEDULED) return 'Pendiente';
  return 'Eliminado';
};

export const getFirstDayOfWeekNumber = (currentDate) => {
  const dayOfWeek = currentDate.getDay();
  const daysPassed = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  return currentDate.getDate() - daysPassed;
};
// example
export const getFirstAndLastDateInTwoWeeks = (currentDate) => {
  const first = getFirstDayOfWeekNumber(currentDate);
  const last = first + 13;

  const month = currentDate.getMonth();
  const year = currentDate.getFullYear();

  const firstDate = new Date(year, month, first);
  const lastDate = new Date(year, month, last);
  return { firstDate, lastDate };
};

export const getDaysInTreeWeeks = (currentDate) => {
  const { firstDate: date } = getFirstAndLastDateInTwoWeeks(currentDate);
  const days = [];
  for (let i = 0; i <= 20; i++) {
    days.push(new Date(date));
    date.setDate(date.getDate() + 1);
  }
  return days;
};

const endDayMonth = (date) => new Date(date.getFullYear(), date.getMonth() + 1, 0);
const countDaysMonth = (date) => endDayMonth(date).getDate();

const completeDaysPrevious = (month) => {
  const dayFirstMonth = new Date(month.getFullYear(), month.getMonth());
  let positionFirstDayMonth = dayFirstMonth.getDay();
  if (positionFirstDayMonth === 1) return [];
  if (positionFirstDayMonth === 0) positionFirstDayMonth = 7;
  const dayInitial = new Date(
    dayFirstMonth.getFullYear(),
    dayFirstMonth.getMonth(),
    getFirstDayOfWeekNumber(dayFirstMonth) - 1
  );
  const days = [];
  for (let index = 1; index < positionFirstDayMonth; index++) {
    const day = new Date(dayInitial.setDate(dayInitial.getDate() + 1));
    days.push(day);
  }
  return days;
};

const completeDaysNext = (month) => {
  const dateFinish = endDayMonth(new Date(month));
  const positionLastDayMonth = dateFinish.getDay();
  if (positionLastDayMonth === 0) return [];
  const positionDayFinish = dateFinish.getDay();
  const days = [];
  for (let index = 0; index <= 6 - positionDayFinish; index++) {
    const day = new Date(new Date(dateFinish).setDate(dateFinish.getDate() + index + 1));
    days.push(day);
  }
  return days;
};

export const getDatesEveryDayCalendar = (month) => {
  const daysCalendar = [];
  const countDays = countDaysMonth(month);
  for (let index = 1; index <= countDays; index++) {
    const nextDay = new Date(month.getFullYear(), month.getMonth(), index);
    daysCalendar.push(nextDay);
  }

  const daysPreviousMonth = completeDaysPrevious(month);
  const daysNextMonth = completeDaysNext(month);
  return [...daysPreviousMonth, ...daysCalendar, ...daysNextMonth];
};

export const getDaysOfMonthlyPeriod = (currentDate) => {
  const dayFirstMonth = new Date(currentDate.getFullYear(), currentDate.getMonth());
  const date = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth(),
    getFirstDayOfWeekNumber(dayFirstMonth)
  );
  const days = [];
  for (let i = 0; i <= 27; i++) {
    days.push(new Date(date));
    date.setDate(date.getDate() + 1);
  }
  if (
    moment(days[days.length - 1])
      .add(1, 'days')
      .toDate()
      .getMonth() === dayFirstMonth.getMonth()
  ) {
    for (let i = 0; i <= 6; i++) {
      days.push(new Date(date));
      date.setDate(date.getDate() + 1);
    }
  }
  return days;
};

export const getFirstAndLastDateInTreeWeeks = (currentDate) => {
  const first = getFirstDayOfWeekNumber(currentDate);
  const last = first + 20;

  const month = currentDate.getMonth();
  const year = currentDate.getFullYear();

  const firstDate = new Date(year, month, first);
  const lastDate = new Date(year, month, last);
  return { firstDate, lastDate };
};

export const getLastDateInTreeWeeks = (currentDate) => {
  const first = getFirstDayOfWeekNumber(currentDate);
  const last = first + 20;

  const month = currentDate.getMonth();
  const year = currentDate.getFullYear();

  const lastDate = new Date(year, month, last);
  return { lastDate };
};

export const getFormatedDate = (date, formatString = 'yyyy-MM-dd') =>
  format(new Date(date), formatString);

export const searchDate = () => {
  const today = new Date();
  const firstDate = new Date();
  firstDate.setDate(today.getDate() + 1);
  const daysInThreWeeks = getLastDateInTreeWeeks(today);
  return [getFormatedDate(firstDate), getFormatedDate(daysInThreWeeks.lastDate)];
};

export const mobileCompability = () => {
  const classNames = [];
  if (navigator.userAgent.match(/(iPad|iPhone|iPod)/i)) classNames.push('device-ios');
  if (navigator.userAgent.match(/android/i)) classNames.push('device-android');

  const html = document.getElementsByTagName('html')[0];

  if (classNames.length) classNames.push('on-device');
  if (html.classList) html.classList.add.apply(html.classList, classNames);
};

export const getMyAdress = (adress, value) => {
  const myadress = {};
  adress[0].address_components.forEach((elm) => {
    if (elm.types[0].indexOf('administrative') !== -1) {
      myadress[elm.types[0]] = elm.long_name;
    }
  });
  myadress.formatted_address = value;
  myadress.other = '';
  myadress.place_id = adress[0].place_id;
  return myadress;
};

export const hashMail = (email) => {
  const arrEmail = email.split('@');
  return `${arrEmail[0].substring(0, 1)}****@${arrEmail[1]}`;
};
export const dataRadioYesNo = (t) => [
  { value: 'true', label: 'Si' },
  { value: 'false', label: 'No' },
];
export const getQrObject = (merchant, name) => ({
  id_merchant: merchant.merchant._id,
  id_business_merchant: merchant.merchant.merchants[0].id,
  description: name,
});
export const getIsChild = (path) => {
  let aux = false;
  NAV.forEach((elm) => {
    if (elm.childs)
      elm.childs.forEach((child) => {
        if (child === path) aux = true;
      });
    if (elm.submenu)
      elm.submenu.forEach((submenu) => {
        if (submenu.childs)
          submenu.childs.forEach((child) => {
            if (child === path) aux = true;
          });
      });
  });
  return aux;
};

const withCero = (date) => {
  if (date < 10) {
    return `0${date}`;
  }
  return date;
};

export const getDateForService = (data) => {
  if (!data) return format(new Date(), 'yyyy/MM/dd');
  return format(new Date(data), 'yyyy/MM/dd');
};

export const dateToShow = (date) => {
  const aux = new Date(date);
  const month = withCero(aux.getMonth() + 1);
  const day = withCero(aux.getDate());

  const response = `${day}/${month}/${aux.getFullYear()} - ${withCero(aux.getHours())}:${withCero(
    aux.getMinutes()
  )}hs`;
  return response;
};

export const getDateForMovements = (data) => {
  if (!data) return format(new Date(), 'dd/MM/yyyy');
  return format(new Date(data), 'dd/MM/yyyy');
};

export const substractOptionArray = (response, value, text) => {
  const aux = response.map((elm) => ({
    value: elm[value],
    text: elm[text],
  }));
  return aux;
};

export const capitalize = (frase) => {
  const aux = frase.split(' ');
  const rta = aux.map((elm) =>
    elm
      .split('')
      .map((elm, i) => {
        if (i === 0) return elm.toUpperCase();
        return elm.toLowerCase();
      })
      .join('')
  );
  return rta.join(' ');
};

export const getTypeToShow = (type) => type;

export const getObjectTransactions = (
  merchant,
  currentPage,
  startDate,
  endDate,
  textFilter,
  perPage
) => {
  const objectQuery = {
    startEventDate: getFormatedDate(startDate),
    endEventDate: getFormatedDate(endDate || new Date()),
    page: currentPage,
    perpage: perPage || 10,
  };
  if (textFilter) {
    objectQuery.saleAuthorizationId = textFilter;
  }
  const query = new URLSearchParams(objectQuery).toString();
  return {
    query,
    merchant_id: merchant.wallet_info?.merchant_id || merchant.merchant_id,
  };
};

export const getObjectFutureTransactions = (
  merchantId = 1,
  // currentPage,
  startDate,
  endDate,
  perPage
) => {
  const objectQuery = {
    startPaymentDate: getFormatedDate(startDate),
    endPaymentDate: getFormatedDate(endDate || new Date()),
    // page: currentPage,
    perpage: perPage || 10,
    fetchAll: true,
    groupBy: 'paymentDate',
  };
  const query = new URLSearchParams(objectQuery).toString();
  return {
    query,
    merchantId,
  };
};

export const getTotalFromBalances = (rta) => {
  const aux2 = Object.keys(rta);
  let total = 0;
  aux2.forEach((elm) => {
    switch (elm) {
      case 'scheduled':
        total += rta[elm].unblocked.net;

        break;
      default:
        break;
    }
  });
  return total;
};

export const getObjectMovements = (
  merchantId = 1,
  startDate,
  endDate,
  textFilter,
  perPage,
  currentPage
) => {
  const objectQuery = {
    page: currentPage || 1,
    perPage: perPage || 10,
    beginDate: getDateForMovements(startDate),
    endDate: getDateForMovements(endDate || new Date()),
  };
  if (textFilter) {
    objectQuery.saleAuthorizationId = textFilter;
  }
  const query = new URLSearchParams(objectQuery).toString();
  return {
    query,
    merchantId,
  };
};

export const getBodyForUploadingFiles = (token, fileList) => {
  const formData = new FormData();
  for (let i = 0; i < fileList.length; i++) {
    formData.append(`file`, fileList[i]);
  }
  return {
    fileList: formData,
    token,
  };
};

export const getTokenFromParams = (location) => location.search.split('?t=')[1];

export const setCountryCurrency = (country) => {};

export const getInitialToken = () => substractToken(window.location.pathname);
const substractToken = (url) => {
  const aux = url.split('/');
  return aux[aux.length - 1];
};

export const isValidToken = (token) => token.length > 30;

export const getClassDocumentation = (status) => {
  switch (status) {
    case DOCUMENT.WITHOUT_SUBMITTING:
      return DOCUMENT.WITHOUT_SUBMITTING_CLASS;
    case DOCUMENT.PENDING:
      return DOCUMENT.PENDING_CLASS;
    case DOCUMENT.APROVED:
      return DOCUMENT.APROVED_CLASS;
    case DOCUMENT.REJECTED:
      return DOCUMENT.REJECTED_CLASS;
    default:
  }
};

export const getTextDocumentation = (status) => {
  switch (status) {
    case DOCUMENT.WITHOUT_SUBMITTING:
      return DOCUMENT.WITHOUT_SUBMITTING_TEXT;
    case DOCUMENT.PENDING:
      return DOCUMENT.PENDING_TEXT;
    case DOCUMENT.APROVED:
      return DOCUMENT.APROVED_TEXT;
    case DOCUMENT.REJECTED:
      return DOCUMENT.REJECTED_TEXT;
    default:
  }
};

export const substractQRSrc = (img) => {
  const aux = img.split("'");
  return aux[1];
};

export const printDiv = (divName, sleep = 100, divPrint = 'print') => {
  const printContents = document.getElementById(divName).innerHTML;
  const printDiv = document.getElementById(divPrint);
  printDiv.innerHTML = printContents;
  setTimeout(() => {
    window.print();
  }, sleep);
};

export const getRegionNumber = (country) => {
  switch (country.toLowerCase()) {
    case 'cl':
      return '56';
    default:
      return '';
  }
};

export const getRegionIdPhone = (phoneNumber) => phoneNumber.substr(0, 1);

export const getSimplePhone = (phoneNumber) => phoneNumber.substr(1, phoneNumber.length);

export const parsePhoneObject = (phoneNumber, countryCode = `+${regionPhoneCode}`) => ({
  country_code: countryCode,
  region_id: getRegionIdPhone(phoneNumber),
  number: getSimplePhone(phoneNumber),
  full_number: `${countryCode}${phoneNumber}`,
});

export const isObject = (param) => typeof param === 'object';

export const changeWebTitle = (title) => {
  document.title = title;
};

export const editObjectConstructor = (data) => {
  const defaultData = {
    business_name: data.business_name,
    address: data.address,
    address_info: data.address_info,
    zip_code: data.zip_code,
    merchant_name: data.merchant_name,
    first_name: data.legal_agent.first_name,
    last_name: `${data.legal_agent.patern_last} ${data.legal_agent.matern_last}`,
    matern_last: data.legal_agent.matern_last,
    patern_last: data.legal_agent.patern_last,
    manager_doc_type: data.legal_agent.document.doc_type,
    manager_doc_number: data.legal_agent.document.doc_number,
    phones: [
      {
        country_code: data.legal_agent.phones[0].country_code,
        region_id: data.legal_agent.phones[0].region_id,
        number: data.legal_agent.phones[0].number,
        full_number: data.legal_agent.phones[0].full_number,
      },
    ],
  };
  return defaultData;
};

export const editObjectConstructorPerson = (data) => {
  const defaultData = {
    business_name: data.business_name,
    address: data.address,
    address_info: data.address_info,
    zip_code: data.zip_code,
    merchant_name: data.merchant_name,
    first_name: data.legal_agent.first_name,
    last_name: `${data.legal_agent.patern_last} ${data.legal_agent.matern_last}`,
    matern_last: data.legal_agent.matern_last,
    patern_last: data.legal_agent.patern_last,
    manager_doc_type: data.document.doc_type,
    manager_doc_number: data.document.doc_number,
    phones: [
      {
        country_code: data.legal_agent.phones[0].country_code,
        region_id: data.legal_agent.phones[0].region_id,
        number: data.legal_agent.phones[0].number,
        full_number: data.legal_agent.phones[0].full_number,
      },
    ],
  };
  return defaultData;
};
export const isFullProfile = (document_status, isSeller = false) => {
  if (isSeller) return true;
  // separar statusDocument de isFullProfile
  const aux = document_status.filter((elm) => {
    if (elm.status === DOCUMENT.REJECTED) return elm;
    if (elm.status !== DOCUMENT.APROVED) {
      if (elm.type === 'pjd6' || elm.type === 'pjt6') {
        if (elm.status !== DOCUMENT.WITHOUT_SUBMITTING) return elm;
      } else {
        return elm;
      }
    }
    return null;
  });

  if (aux.length > 0) return false;
  return true;
};

export function debounce(fn, ms) {
  let timer;
  return function (...args) {
    clearTimeout(timer);
    timer = setTimeout(() => {
      timer = null;
      fn.apply(this, args);
    }, ms);
  };
}

export const getColor = (status) => {
  const chipcolor =
    status === 'SETTLED' ? 'var(--grass)' : status === 'Rechazado' ? '#eb3e74' : '#e7a703';
  const chipbackground =
    status === 'SETTLED' ? '#ecf7e9' : status === 'Rechazado' ? '#fde5ec' : '#fff8e5';
  return { chipcolor, chipbackground };
};

export const dateDifference = (oldDate, newDate = new Date()) => {
  oldDate = !(oldDate instanceof Date) ? new Date(oldDate) : oldDate;
  return Math.floor((newDate - oldDate) / (1000 * 60 * 60 * 24));
};

/**
 * Returns `true` if `dateLeft` is greater than `dateRight`, `false` otherwise.
 * This function do calculations based on days ignoring hours/min/seconds.
 *
 * @param {Date} dateLeft
 * @param {Date} dateRight
 * @returns {boolean}
 */
export const isDateGreaterThan = (dateLeft, dateRight) => {
  dateRight = endOfDay(dateRight); // the calculation is done for end of day (time=23:59:59)
  return dateLeft > dateRight;
};

export const countDifferenceDays = (oldDate, newDate = new Date()) =>
  /** Comparacion hora local: falta definir a considerar utc-0 */
  differenceInCalendarDays(newDate, oldDate);

export const canRefund = (
  operation,
  eventDate,
  maxDaysToRefund = REGIONS[country].daysToRefund
) => {
  if (operation.toLowerCase().includes('reversa') || operation.toLowerCase().includes('canc')) {
    return false;
  }

  const isWithinRange = countDifferenceDays(new Date(eventDate), new Date()) <= maxDaysToRefund;

  let paymentMethod = getMethod(operation);
  switch (paymentMethod) {
    case TYPE_CARD.DEBIT_CARD:
    case TYPE_CARD.PREPAY:
      paymentMethod = 'debit';
      break;
    case TYPE_CARD.CREDIT_CARD:
      paymentMethod = 'credit';
      break;
    case TYPE_CARD.FPAY_BALANCE:
      paymentMethod = 'fpay-account';
      break;
    case TYPE_CARD.POINT_PAYMENTS:
      paymentMethod = 'point';
      break;
    case TYPE_CARD.PAYMENT_WITH_YAPE:
      paymentMethod = 'yape';
      break;
    default:
      paymentMethod = null;
  }

  const canRefundPaymentMethod = paymentMethodsValidToRefund.indexOf(paymentMethod) !== -1;
  return isWithinRange && canRefundPaymentMethod;
};

export const mapStatus = (status) => {
  switch (status) {
    case 'VERIFY EMAIL':
      return 'PENDIENTE';
    case 'UNSUBSCRIBE':
      return 'BLOQUEADO';
    case 'APPROVED':
      return 'ACTIVO';
    default:
      return status;
  }
};

export const dateToDescription = (date, longDescription = false) => {
  const months = [
    'Enero',
    'Febrero',
    'Marzo',
    'Abril',
    'Mayo',
    'Junio',
    'Julio',
    'Agosto',
    'Septiembre',
    'Octubre',
    'Noviembre',
    'Diciembre',
  ];
  const number = date.getDate();
  const month = months[date.getMonth()];

  if (longDescription) return `${number} de ${month}`;
  return `${number} ${month}`;
};

export const removeAccents = (str) => {
  const accents = 'ÀÁÂÃÄÅàáâãäåÒÓÔÕÕÖØòóôõöøÈÉÊËèéêëðÇçÐÌÍÎÏìíîïÙÚÛÜùúûüÑñŠšŸÿýŽž';
  const accentsOut = 'AAAAAAaaaaaaOOOOOOOooooooEEEEeeeeeCcDIIIIiiiiUUUUuuuuNnSsYyyZz';
  str = str.split('');
  const strLen = str.length;
  let i;
  let x;
  for (i = 0; i < strLen; i++) {
    if ((x = accents.indexOf(str[i])) !== -1) {
      str[i] = accentsOut[x];
    }
  }
  return str.join('');
};

export const downloadFile = (linkDonwload, fileName) => {
  const link = document.createElement('a');
  link.download = linkDonwload;
  link.href = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const escapeCsvField = (value) => {
  if (value === null || value === undefined) return '';
  const str = String(value);
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

export const generateCsvAndDownload = (headers, rows, filename) => {
  const lines = [
    headers.map(escapeCsvField).join(','),
    ...rows.map((row) => row.map(escapeCsvField).join(',')),
  ];
  const blob = new Blob(['\uFEFF', lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const downloadPlugin = (link, fileName) => {
  const a = document.createElement('a');
  document.body.appendChild(a);
  a.style = 'display:none';
  a.href = link;
  a.download = fileName;
  a.click();
  document.body.removeChild(a);
};

export const dataTestIdFormatter = (str) => {
  if (str) {
    return removeAccents(str).toLowerCase().replace(/ /g, '-').replace(/\//g, '-');
  }
};

/** genera 10/11 caracteres alfanumericos */
export const generateRandomString = (length = 16) => {
  const array = new Uint8Array(length);
  window.crypto.getRandomValues(array);

  return Array.from(array)
    .map((b) => (b % 36).toString(36))
    .join('');
};

/**
 * genera la cantidad de caracteres alfanumericos definido por parametro
 * @param {*} length int: longitud del random generado
 */
export const generateRandomToLength = (length = 64) => {
  let random = generateRandomString();

  for (let count = 0; count < Math.trunc(length / 10); count++) {
    random += generateRandomString();
  }

  return random.slice(-length);
};



export const fogEmail = (email) =>
  email.replace(
    /[a-z0-9\-_.]+@/gi,
    (c) =>
      `${
        c.substr(0, 3) +
        c
          .split('')
          .slice(3, -1)
          .map((v) => '*')
          .join('')
      }@`
  );

export const getLimitDays = (merchant, config) =>
  merchant.seller === sellerTypes.ON_US ||
  merchant.seller === sellerTypes.ALIANZA ||
  merchant.seller === sellerTypes.ALIANZAMIXTO
    ? config?.transactions?.limit_days?.on_us
    : config?.transactions?.limit_days?.on_them;

export const getInitialFilter = (merchant, config) => ({
  text: null,
  startDate: addDays(new Date(), -getLimitDays(merchant, config) + 1),
  endDate: new Date(),
  filterApply: false,
});

export const getInstallments = (installment, installmentTotal) => {
  if (installmentTotal === 1) {
    return installment;
  }
  return `${installment}/${installmentTotal}`;
};
export const getRolPriority = (user) =>
  user.is_legal_agent
    ? ROLES_LIST_PRIORITY['Representante Legal']
    : ROLES_LIST_PRIORITY[user.roles[0]];

export const getValidDateRange = (dateRange = {}, minDate, maxDate) => {
  const { init: initDateRange, finish: finishDateRange } = dateRange;
  // Validate undefined dates
  if (!initDateRange || !finishDateRange) return dateRange;

  return {
    init: minDate > initDateRange ? minDate : initDateRange,
    finish: maxDate < finishDateRange ? maxDate : finishDateRange,
  };
};

const keysToSearchWidget = {
  'LEGAL ENTITY': 'persona_juridica',
  'NOT LEGAL ENTITY': 'persona_natural',
  ADMIN: 'superadmin',
  'ADMIN-SECONDARY': 'admin',
  SELLER: 'vendedor',
};

export const convertValueToWidget = (nameKeyToChange, keyToChange) => {
  if (nameKeyToChange === 'seller') return keyToChange === 'gsc' ? 'seller' : 'merchant';
  if (nameKeyToChange === 'one_day_company') return keyToChange ? 'company_1' : 'company_0';
  if (nameKeyToChange === 'is_legal_agent') return keyToChange ? 'agent_1' : 'agent_0';

  return keysToSearchWidget[keyToChange] || keyToChange;
};

export const callWidgetLoyalink = ({ npsWidget, merchant }) => {
  const event = new Event(widgetEvent, { cancelable: true });
  npsWidget
    .map((campo) =>
      window[widgetTag].functions.addExtras(
        campo.idAlco,
        convertValueToWidget(campo.value, get(merchant, campo.value))
      )
    )
    .join('');
  setTimeout(() => document.dispatchEvent(event), 1000);
  return event;
};

export const getConfigMerchantType = () => {
  const {
    login: {
      merchant: { seller },
    },
  } = store.getState();

  if (!seller || seller === '') return CONFIG_MERCHANT_TYPE.ON_THEM;
  return CONFIG_MERCHANT_TYPE[seller.toUpperCase()] ?? CONFIG_MERCHANT_TYPE.SELLER;
};

export const getFetchRefundsDateRange = (eventDate) => {
  const configMerchantType = getConfigMerchantType();
  const { config } = store.getState();

  const refundsLimitDays = config?.transactions?.get_sale_refunds_limit_days ?? {};
  const getSaleRefundsLimitDays = refundsLimitDays[configMerchantType] ?? refundsLimitDays.on_them;

  if (!getSaleRefundsLimitDays) return {};

  const startEventDate = new Date(eventDate);
  const endEventDate = moment(eventDate).add(getSaleRefundsLimitDays, 'days');

  return {
    startEventDate: moment(startEventDate).format('YYYY-MM-DD'),
    endEventDate: moment(endEventDate).format('YYYY-MM-DD'),
  };
};

export const splitText = (toSplit, textNumber) => {
  const newString =
    toSplit.length > textNumber ? `${toSplit.substring(0, textNumber)}...` : toSplit;
  return newString;
};
